import { LinkReceiver, StreamParser, TransferCollector, tee, viceRawToWire } from './src/core/protocol.js';
import { Mps803Decoder } from './src/core/mps803.js';
import { lineToText, lineToSegments } from './src/render/render_text.js';
import { buildPdf, paperPdfOptions } from './src/render/render_pdf.js';
import { pieDash, quotaView } from './src/server/quota.js';
import { CELL_Y, needleRender, grayToRGBA } from './src/render/render_needle.js';
import { createFavicon } from './src/render/favicon.js';
import { CMD, DeviceLink, PROTOCOL_VERSION, buildFrame, setupState } from './src/device/device.js';
import { openLedWizard } from './src/device/ledwizard_ui.js';
import { openButtonWizard } from './src/device/buttonwizard_ui.js';
import { openIecWizard } from './src/device/iecwizard_ui.js';
import { openFlasher } from './src/device/flasher_ui.js';
import { PORT_FILTERS } from './src/device/flasher.js';
import { timestampName, baseName, fileNames } from './src/core/filenames.js';
import { assetUrl } from './src/core/assets.js';
import { buildReplayDisk } from './src/device/replay.js';
import { JobBook, WHY_TEXT, applyEvent, replayEvents } from './src/core/jobs.js';
import { openJobStore } from './src/core/jobstore.js';
import { openDb } from './src/core/db.js';
import { openKeyStore } from './src/server/keystore.js';
import { deriveFromSecret, parseLoginFragment } from './src/server/crypto.js';
import { ReaderClient } from './src/server/serverclient.js';
import { syncNotifications } from './src/server/push.js';
import { ServerSource } from './src/server/serversource.js';
import { addFromLoginText, openPrintersDialog, printerLabel } from './src/server/printers_ui.js';
import { openWifiSetup } from './src/device/wifisetup_ui.js';
import { openTestPrint } from './src/device/testprint_ui.js';
import { previewRegion, previewGray, dirtyTargetRows } from './src/render/preview.js';
import { loadFont, renderPages, contentRows, toRGBA, PAGE_BREAK, FontMissingError } from './src/render/render_bitmap.js';

const USB_FILTERS = PORT_FILTERS; // the board's CP210x or CH340 USB-UART bridge

const $ = (id) => document.getElementById(id);
const transfers = [];
const wire = []; // raw chunks as received, for debugging/replay
let wireBytes = 0; // their total size
const lines = []; // decoded printer lines (IR, see mps803.js) and PAGE_BREAK markers
let port = null;
let writer = null; // locked writer of the open port
let device = null; // DeviceLink: commands to the ESP while connected
let pinPressListener = null; // set by the button wizard while it is open
let restarting = false; // the ESP is restarting on purpose (saved a setting): reconnect, do not report an error

// Pico CSS is an npm dependency (web/node_modules); say so instead of silently
// showing an unstyled page when `make install` (in web/) was never run.
{
  const css = $('pico-css');
  const showMissing = () => ($('npm-error').hidden = false);
  // A failed load leaves a sheet with no rules (Chromium) or no sheet at all.
  let rules = 0;
  try {
    rules = css.sheet?.cssRules.length ?? 0;
  } catch {
    rules = 0;
  }
  if (rules === 0) showMissing();
  css.addEventListener('error', showMissing);
}

if (!('serial' in navigator)) {
  $('unsupported').textContent = window.isSecureContext
    ? 'This browser has no Web Serial API. Use Chrome, Edge or Vivaldi.'
    : `Web Serial needs a secure context. Open this page via http://localhost:PORT or https, not ${location.origin}.`;
  $('unsupported').hidden = false;
  $('connect').disabled = true;
}

// Pico modal as a promise-based confirm(). Esc, the close button, a click on the
// backdrop and Cancel all resolve false; only the confirm button resolves true.
// Focus starts on Cancel so Enter never discards data by accident.
function askConfirm({ title, message, confirmLabel = 'Confirm' }) {
  const dialog = $('confirm');
  const root = document.documentElement;
  $('confirm-title').textContent = title;
  $('confirm-message').textContent = message;
  $('confirm-ok').textContent = confirmLabel;
  return new Promise((resolve) => {
    const finish = () => {
      dialog.removeEventListener('close', finish);
      dialog.removeEventListener('click', onBackdrop);
      root.classList.remove('modal-is-open', 'modal-is-opening');
      resolve(dialog.returnValue === 'confirm');
    };
    const onBackdrop = (e) => {
      if (e.target === dialog) dialog.close('cancel'); // click outside the article
    };
    dialog.addEventListener('close', finish);
    dialog.addEventListener('click', onBackdrop);
    dialog.returnValue = '';
    root.classList.add('modal-is-open', 'modal-is-opening');
    setTimeout(() => root.classList.remove('modal-is-opening'), 400);
    dialog.showModal();
    $('confirm-cancel').focus();
  });
}
$('confirm-ok').onclick = () => $('confirm').close('confirm');
$('confirm-cancel').onclick = () => $('confirm').close('cancel');
$('confirm-close').onclick = () => $('confirm').close('cancel');

// ---- print jobs ----
// A job is everything one print run produced (jobs.js). The page shows one job at a
// time (`selected`); the live job is the one the C64 is printing into. While the user
// has not picked an older job (`following`), a new job takes over the view by itself.
// The tab icon (and the logo) shows the state of what is on show: the USB device (port open or not, bytes
// arriving) or the selected print server printer (online from its last hello or chunk, data arriving).
let usbConnected = false;
let serverOnline = false;
let iconOnline = null;
function syncIcon(force = false) {
  const want = source.kind === 'server' ? serverOnline : usbConnected;
  if (!force && want === iconOnline) return; // setConnected blanks the sheet: only on a real change
  iconOnline = want;
  favicon.setConnected(want);
}
const SERVER_ACTIVE_MS = 4000; // a print server delivers a whole flush at once: animate a while after it

let selected = null;
let following = true;

// ---- print job name: used for every download ----
// Empty input = the date and time of the first data of the job (shown as the
// placeholder). The name belongs to the selected job.
const defaultJobName = () => timestampName(selected?.startedAt ?? new Date());
const jobName = () => baseName($('job-name').value, defaultJobName());
function refreshJobNamePlaceholder() {
  $('job-name').placeholder = defaultJobName();
}
let renameTimer = 0;
$('job-name').oninput = () => {
  const job = selected;
  if (job) {
    const name = $('job-name').value.trim() || null;
    job.name = name;
    job.nameDirty = true; // a poll must not put the older name from the server back while we are typing
    clearTimeout(renameTimer);
    // a server job is renamed over the network: wait until the typing pauses
    renameTimer = setTimeout(() => source.rename(job, name).catch(sourceError), source.kind === 'server' ? 700 : 0);
    renderJobList();
  }
  refreshJobNamePlaceholder();
};
refreshJobNamePlaceholder();

function download(name, parts) {
  const url = URL.createObjectURL(new Blob(parts, { type: 'application/octet-stream' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The ESP buffers 16 KB (RING_SIZE in firmware/main/stream.c) of the stream while no
// browser reads it: a print that is smaller than that would have fit.
const RING_BYTES = 16384;
// Average wire rate over the last RATE_WINDOW_MS, from (time, wireBytes) samples.
const RATE_WINDOW_MS = 5000;
const rateSamples = [];
function wireKBps() {
  const now = performance.now();
  if (rateSamples.length && wireBytes < rateSamples[rateSamples.length - 1].bytes) rateSamples.length = 0; // Clear
  rateSamples.push({ t: now, bytes: wireBytes });
  while (rateSamples.length > 1 && now - rateSamples[0].t > RATE_WINDOW_MS) rateSamples.shift();
  const first = rateSamples[0];
  const dt = now - first.t;
  return dt > 0 ? (wireBytes - first.bytes) / dt : 0; // bytes per ms = KB/s (1000 bytes)
}
function updateRawSize() {
  const data = selected?.dataBytes ?? selected?.serverBytes ?? 0;
  const onWire = selected?.wireBytes ?? 0;
  const pct = Math.round((onWire / RING_BYTES) * 100);
  $('raw-size').textContent =
    `${data.toLocaleString()} bytes from the C64, ${onWire.toLocaleString()} bytes on wire, ${pct}% of ring buffer, ${wireKBps().toFixed(1)} KB/s`;
}
// Keeps the rate falling to 0 when the data stops.
setInterval(updateRawSize, 500);

function render() {
  updateRawSize();
  const body = $('transfers');
  body.replaceChildren();
  transfers.forEach((t, i) => {
    const tr = document.createElement('tr');
    const cells = [i + 1, t.channel, t.data.length, t.eoi ? 'yes' : 'no'];
    for (const c of cells) tr.append(Object.assign(document.createElement('td'), { textContent: c }));
    const td = document.createElement('td');
    const b = Object.assign(document.createElement('button'), { textContent: 'Download', className: 'outline' });
    b.onclick = () => download(fileNames.rawTransfer(jobName(), i + 1), [t.data]);
    td.append(b);
    tr.append(td);
    body.append(tr);
  });
  const any = transfers.length > 0;
  $('transfers-count').textContent = any ? `${transfers.length} ${transfers.length === 1 ? 'transfer' : 'transfers'}` : '';
  $('dl-all').disabled = !any;
  $('dl-wire').disabled = wire.length === 0;
  $('dl-replay').disabled = !((selected?.dataBytes ?? 0) > 0 || (selected?.serverBytes ?? 0) > 0);
  updateVisibility();
}

// What the connected device still needs (setupState of its configuration), or null
// while unknown (not connected, or firmware without the command channel).
let setup = null;

function showSetup(state) {
  setup = state;
  const needs = state !== null && !state.ready; // solid red until the device is set up
  $('setup-summary').classList.toggle('needs-setup', needs);
  $('setup-summary').classList.toggle('outline', !needs);
  for (const [id, done] of [['button', state?.button], ['led', state?.led], ['iec', state?.bus]]) {
    $(`todo-${id}`).hidden = !!done;
    $(`done-${id}`).hidden = !done;
  }
  updateVisibility();
}

// Show the "waiting" hint until something arrives, then the pages and text.
function updateVisibility() {
  const has = lines.length > 0 || decoder.pending().items.length > 0 || transfers.length > 0;
  $('clear').disabled = source.jobs.length === 0 && (source.kind === 'server' || wire.length === 0);
  $('new-job').disabled = !source.current;
  $('jobs-section').hidden = source.jobs.length === 0;
  $('page-end').disabled = fontFailed || !selected || selected.remote === true; // a server job is read-only
  const needsSetup = setup !== null && !setup.ready;
  $('setup-needed').hidden = has || !needsSetup;
  $('empty').hidden = has || needsSetup;
  $('pages-section').hidden = !has;
  $('text-section').hidden = !has;
  $('name-row').hidden = !has;
}

const favicon = createFavicon($('favicon'), $('logo'));

const collector = new TransferCollector((transfer) => {
  transfers.push(transfer);
  render();
});
const decoder = new Mps803Decoder({
  onLine: (l) => {
    lines.push(l);
    renderText();
    scheduleRender();
  },
});

// What the page shows is built from the events of the selected job: live as they
// arrive, or all at once (loadHandlers) when a job is selected.
function showPageBreak(live) {
  if (live) favicon.pageBreak(); // the hardware button arrives here too
  lines.push(PAGE_BREAK);
  renderText();
  scheduleRender();
}
const liveHandlers = tee(collector.handlers(), decoder.handlers(), { onPageBreak: () => showPageBreak(true) });
const loadHandlers = tee(collector.handlers(), decoder.handlers(), { onPageBreak: () => showPageBreak(false) });

function resetDisplay() {
  transfers.length = 0;
  collector.reset();
  lines.length = 0;
  decoder.reset();
  lastPageCount = 0; // a different job is not a page break
  renderText();
  scheduleRender();
}

// Shows a job: its events are replayed into the display. A job from a print server may still have to
// be fetched; events that arrive meanwhile are not applied live (the replay brings everything).
let selectSeq = 0;
let loading = null;
async function selectJob(job, { follow = job === source.current } = {}) {
  selected = job;
  following = follow;
  const seq = ++selectSeq;
  $('job-name').value = job?.name ?? '';
  refreshJobNamePlaceholder();
  resetDisplay();
  renderJobList();
  if (job && job.remote && !job.loaded) {
    loading = job;
    setSourceStatus('loading the job…');
    try {
      await source.load(job);
    } catch (e) {
      sourceError(e);
      return;
    } finally {
      if (loading === job) loading = null;
    }
    if (seq !== selectSeq) return; // somebody else was selected meanwhile
    setSourceStatus('');
  }
  if (job) replayEvents(job.events, loadHandlers);
  render();
  renderJobList();
}

// ---- storing jobs (IndexedDB) ----
const MAX_JOBS = 100;
const MAX_STORED_BYTES = 64 * 1024 * 1024;
// null when the browser has no IndexedDB: jobs are then only kept in memory.
const opened = typeof indexedDB !== 'undefined' ? openDb() : null;
opened?.catch((e) => console.warn('IndexedDB is not usable, jobs are not stored', e));
const store = opened ? openJobStore(opened) : null;
const keyStore = opened ? openKeyStore(opened) : null;
const dirty = new Set();
function markDirty(job) {
  if (store) dirty.add(job);
}
async function flushJobs() {
  if (!store || dirty.size === 0) return;
  const jobs = [...dirty].filter((j) => book.jobs.includes(j));
  dirty.clear();
  try {
    for (const j of jobs) await store.save(j);
  } catch (e) {
    console.warn('storing jobs failed', e);
  }
}
setInterval(flushJobs, 2000);
addEventListener('pagehide', flushJobs);
document.addEventListener('visibilitychange', () => document.hidden && flushJobs());

const book = new JobBook({
  onStart: (job) => {
    if (source !== usbSource) return; // the USB device keeps recording while a server printer is shown
    if (following) selectJob(job, { follow: true });
    else renderJobList();
    book.trim(MAX_JOBS, MAX_STORED_BYTES);
  },
  onEvent: (job, ev) => {
    if (source !== usbSource) return;
    if (job === selected) applyEvent(ev, liveHandlers);
    scheduleJobList();
  },
  onChange: markDirty,
  onRemove: (jobs) => {
    store?.remove(jobs.map((j) => j.id)).catch((e) => console.warn('removing jobs failed', e));
    if (source !== usbSource) return;
    if (jobs.includes(selected)) selectJob(null, { follow: true });
    else renderJobList();
  },
});

// Where the jobs on show come from: the USB device (the JobBook above) or a printer on a print server.
// Both have jobs, current (the open job), load(job), remove(jobs), closeCurrent(), clear(), rename(job, name).
const usbSource = {
  kind: 'usb',
  get jobs() {
    return book.jobs;
  },
  get current() {
    return book.current;
  },
  async load() {},
  async remove(jobs) {
    const ids = jobs.map((j) => j.id);
    book.remove(ids);
    await store?.remove(ids).catch((e) => console.warn('removing jobs failed', e));
  },
  async closeCurrent() {
    book.closeCurrent();
  },
  async clear() {
    book.clear();
    dirty.clear();
    await store?.clear().catch((e) => console.warn('clearing the stored jobs failed', e));
  },
  async rename(job, name) {
    job.name = name;
    job.nameDirty = false;
    markDirty(job);
  },
};
let source = usbSource;

// Jobs from earlier visits; must be in before the first live data so ids do not clash.
const storedLoaded = (async () => {
  if (!store) return;
  try {
    for (const job of await store.loadAll()) book.adopt(job);
    if (source !== usbSource) return;
    const removed = book.trim(MAX_JOBS, MAX_STORED_BYTES);
    if (removed.length) await store.remove(removed.map((j) => j.id));
    const newest = book.jobs[book.jobs.length - 1];
    if (newest && !selected) selectJob(newest, { follow: true }); // follow: the next print takes over
    else renderJobList();
  } catch (e) {
    console.warn('loading stored jobs failed', e);
  }
})();

// The stream of the open port: frames are checked and acknowledged by `linkRx` (one per connection, see attachPort),
// its payload is the plain stream of firmware/main/stream.h.
let linkRx = null;
function onStreamBytes(value) {
  wire.push(value);
  wireBytes += value.length;
  parser.push(value);
  renderText();
  updateRawSize();
  if (wire.length === 1) render();
}
const parser = new StreamParser(
  tee(book.handlers(), {
    onHello: (v) => setStatus(`connected (protocol v${v})`, 'ok'),
    onReset: () => setStatus('connected (C64 reset)', 'ok'),
    onResponse: (bytes) => device?.handleResponse(bytes), // answers to device commands
    onPinPress: (pin) => pinPressListener?.(pin), // button discovery
    onData: () => source === usbSource && favicon.pulse(), // animate the tab icon while the C64 prints
    onListen: () => source === usbSource && favicon.pulse(),
  }),
);

// ---- the job list ----
let jobListTimer = 0;
function scheduleJobList() {
  if (jobListTimer) return;
  jobListTimer = setTimeout(() => {
    jobListTimer = 0;
    renderJobList();
  }, 500);
}

// Size shown for a job: the data bytes, or (a server job that has not been fetched) its stored size.
const jobSize = (job) => (job.dataBytes === null ? `~${job.serverBytes.toLocaleString()} bytes` : `${job.dataBytes.toLocaleString()} bytes`);

function renderJobList() {
  $('jobs-section').hidden = source.jobs.length === 0;
  $('new-job').disabled = !source.current;
  const body = $('job-list');
  body.replaceChildren();
  for (const job of [...source.jobs].reverse()) {
    const tr = document.createElement('tr');
    tr.className = job === selected ? 'selected' : '';
    tr.title = job.endWhy === null ? 'Still open' : `Ended by: ${WHY_TEXT[job.endWhy] ?? job.endWhy}`;
    const live = job === source.current ? '● live' : '';
    const cells = [
      live,
      `#${job.id}`,
      job.name ?? job.startedAt.toLocaleString(),
      `${WHY_TEXT[job.why] ?? job.why}${job.interrupted ? ' · interrupted' : ''}`,
      jobSize(job),
    ];
    for (const c of cells) tr.append(Object.assign(document.createElement('td'), { textContent: c }));
    const td = document.createElement('td');
    const del = Object.assign(document.createElement('button'), { textContent: 'Delete', className: 'outline secondary compact' });
    del.onclick = (e) => {
      e.stopPropagation();
      deleteJob(job);
    };
    td.append(del);
    tr.append(td);
    tr.onclick = () => selectJob(job);
    body.append(tr);
  }
}

async function deleteJob(job) {
  const ok = await askConfirm({
    title: 'Delete this job?',
    message: source.kind === 'server' ? 'Its data is deleted from the print server. Download what you need first.' : 'Its pages, text and raw data are discarded. Download what you need first.',
    confirmLabel: 'Delete',
  });
  if (!ok) return;
  const wasSelected = job === selected;
  try {
    await source.remove([job]);
  } catch (e) {
    sourceError(e);
    return;
  }
  if (wasSelected) {
    const newest = source.jobs[source.jobs.length - 1] ?? null;
    selectJob(newest, { follow: newest === null || newest === source.current });
  } else {
    renderJobList();
    render();
  }
}

$('new-job').onclick = async () => {
  try {
    await source.closeCurrent(); // the next print starts a new job
  } catch (e) {
    sourceError(e);
  }
  renderJobList();
  render();
};

// ---- print server printers ----
const SOURCE_KEY = 'printy.source';
const setSourceStatus = (text, isError = false) => {
  $('source-status').textContent = text;
  $('source-status').style.color = isError ? 'var(--pico-del-color, #c33)' : '';
};
const sourceError = (e) => {
  console.warn('print server', e);
  setSourceStatus(e.status === 401 ? 'The print server does not know this key (yet).' : `Print server: ${e.message}`, true);
};

async function startServerSource(rec) {
  const keys = await deriveFromSecret(rec.kind, rec.secret);
  const mine = new ServerSource({
    client: new ReaderClient({ keys }),
    onChange: () => {
      if (source !== mine) return;
      setSourceStatus(`${mine.jobs.length} ${mine.jobs.length === 1 ? 'job' : 'jobs'} on the print server`);
      if (selected && !mine.jobs.includes(selected)) {
        // the selected job is gone (deleted elsewhere): show the newest one
        const newest = mine.jobs[mine.jobs.length - 1] ?? null;
        selectJob(newest, { follow: newest === null || newest === mine.current });
      } else if (following && mine.current && selected !== mine.current) {
        selectJob(mine.current, { follow: true });
      } else {
        renderJobList();
      }
      render();
    },
    onEvent: (job, ev) => {
      if (source === mine && job === selected && loading !== job) applyEvent(ev, liveHandlers);
      scheduleJobList();
    },
    onError: (e) => source === mine && sourceError(e),
    onDevice: (online) => {
      if (source !== mine) return;
      serverOnline = online;
      syncIcon();
    },
    onActivity: () => source === mine && favicon.pulse(SERVER_ACTIVE_MS),
    onQuota: (stored, quota) => source === mine && showQuota(stored, quota),
  });
  return mine;
}

// The printer's storage on the print server: a pie and "0.3 MB / 20 MB" beside the printer selector.
function showQuota(stored, quota) {
  const view = quotaView(stored, quota);
  $('quota').hidden = !view;
  if (!view) return;
  $('quota').dataset.level = view.level;
  $('quota').title = view.title;
  $('quota-pie').setAttribute('stroke-dasharray', pieDash(view.fraction));
  $('quota-text').textContent = view.text;
}

// Switches what is on show. rec: a record of the key store, or null for the USB device.
async function activateSource(rec) {
  if (source.kind === 'server') source.stop();
  selected = null;
  following = true;
  ++selectSeq;
  loading = null;
  if (rec) {
    setSourceStatus('connecting…');
    try {
      source = await startServerSource(rec);
    } catch (e) {
      source = usbSource;
      sourceError(e);
    }
  } else {
    source = usbSource;
    setSourceStatus('');
  }
  serverOnline = false; // unknown until the printer answers
  $('quota').hidden = true; // likewise
  syncIcon(true);
  localStorage.setItem(SOURCE_KEY, source.kind === 'server' ? rec.id : 'usb');
  resetDisplay();
  render();
  renderJobList();
  if (source.kind === 'server') {
    source.start();
  } else {
    const newest = book.jobs[book.jobs.length - 1];
    if (newest) selectJob(newest, { follow: newest === book.current });
  }
  await refreshSourceSelect();
}

async function refreshSourceSelect() {
  const recs = keyStore ? (await keyStore.loadAll().catch(() => [])).sort((a, b) => a.added - b.added) : [];
  const select = $('source');
  select.replaceChildren(Object.assign(document.createElement('option'), { value: 'usb', textContent: 'USB device' }));
  for (const rec of recs) select.append(Object.assign(document.createElement('option'), { value: rec.id, textContent: `Wi-Fi: ${printerLabel(rec)}` }));
  const active = source.kind === 'server' ? source.client.keys.id : 'usb';
  select.value = recs.some((r) => r.id === active) || active === 'usb' ? active : 'usb';
  $('source-label').hidden = recs.length === 0;
}

$('source').onchange = async () => {
  const id = $('source').value;
  const rec = id === 'usb' ? null : (await keyStore.loadAll()).find((r) => r.id === id);
  await activateSource(rec ?? null);
};
$('printers').onclick = () =>
  openPrintersDialog({
    keyStore,
    getActive: () => (source.kind === 'server' ? source.client.keys.id : null),
    onUse: (rec) => activateSource(rec),
    onChanged: refreshSourceSelect,
    getDevice: () => device,
  });
$('printers').disabled = !keyStore;

// A login link (https://server/#login=reader:KEY,...) adds the printers it carries, then the page
// shows the last one. The fragment never reaches the server; it is removed from the address at once.
const startupSource = (async () => {
  if (!keyStore) return;
  let target = null;
  try {
    if (parseLoginFragment(location.hash).length > 0) {
      const added = await addFromLoginText(keyStore, location.hash);
      history.replaceState(null, '', location.pathname + location.search);
      if (added.length) target = added[added.length - 1];
    }
    await storedLoaded;
    // a notification opened the page for one printer: #printer=ID
    const wanted = /^#printer=([a-z2-7]{16})$/.exec(location.hash)?.[1];
    if (wanted) {
      history.replaceState(null, '', location.pathname + location.search);
      target = (await keyStore.loadAll()).find((r) => r.id === wanted) ?? target;
    }
    if (!target && localStorage.getItem(SOURCE_KEY) !== null && localStorage.getItem(SOURCE_KEY) !== 'usb') {
      target = (await keyStore.loadAll()).find((r) => r.id === localStorage.getItem(SOURCE_KEY)) ?? null;
    }
  } catch (e) {
    console.warn('starting the print server source failed', e);
  }
  await (target ? activateSource(target) : refreshSourceSelect());
})();

// Notifications (push.js, sw.js): keep the subscriptions of the printers this browser follows up to date, silently
// (it never asks anything), and show the printer when the user clicks a notification while the page is open.
(async () => {
  if (!keyStore) return;
  try {
    await startupSource;
    const recs = (await keyStore.loadAll()).filter((r) => r.notify);
    const followed = await Promise.all(recs.map(async (rec) => ({ rec, client: new ReaderClient({ keys: await deriveFromSecret(rec.kind, rec.secret) }) })));
    for (const { rec } of await syncNotifications({ followed })) await keyStore.save({ ...rec, notify: false }); // no permission any more
  } catch (e) {
    console.warn('notifications could not be refreshed', e);
  }
})();
navigator.serviceWorker?.addEventListener('message', async (event) => {
  if (event.data?.type !== 'show-printer' || !keyStore) return;
  const rec = (await keyStore.loadAll()).find((r) => r.id === event.data.printer);
  if (rec) activateSource(rec);
});

function textContent() {
  const all = lines.map(lineToText);
  const pending = lineToText(decoder.pending());
  if (pending) all.push(pending);
  return all.join('\n');
}

// Plain text, for the .txt download (reverse video has no plain-text form).
// The on-screen view below shows reverse characters with inverted colours.
let textRendered = 0; // entries of `lines` already shown in the text view
let pendingEl = null; // the unfinished line (no CR yet), redrawn on each update

function lineElement(line) {
  const div = document.createElement('div');
  div.className = 'tl';
  if (line.pageBreak) {
    div.className = 'tl pb';
    div.textContent = '── page end ──';
    return div;
  }
  for (const seg of lineToSegments(line)) {
    const span = document.createElement('span');
    span.textContent = seg.text;
    if (seg.reverse) span.className = 'rev';
    else if (seg.note) span.className = 'note';
    div.append(span);
  }
  return div;
}

// Append-only like the paper: finished lines are never rebuilt, only new ones
// are added and the one still being printed is redrawn.
function renderText() {
  const out = $('text');
  const stick = out.scrollTop + out.clientHeight >= out.scrollHeight - 4;
  if (lines.length < textRendered) {
    // everything was cleared
    out.replaceChildren();
    textRendered = 0;
    pendingEl = null;
  }
  pendingEl?.remove();
  pendingEl = null;
  const fragment = document.createDocumentFragment();
  for (; textRendered < lines.length; textRendered++) fragment.append(lineElement(lines[textRendered]));
  const pending = decoder.pending();
  if (pending.items.length > 0) {
    pendingEl = lineElement(pending);
    fragment.append(pendingEl);
  }
  out.append(fragment);
  if (stick) out.scrollTop = out.scrollHeight;
  $('dl-text').disabled = lines.length === 0 && pending.items.length === 0;
  updateVisibility();
}

// ---- page rendering (needs the MPS-803 font; fails loudly without it) ----
let font = null;
let fontFailed = false;
let renderTimer = null;
let lastPages = []; // bitmaps of the pages currently shown
let lastPageRows = 792;

// FONT_URL is injected by the bundler (tools/build.mjs) with the content-hashed name of
// the font file; when running the unbundled sources it is undefined.
loadFont(typeof FONT_URL !== 'undefined' ? FONT_URL : undefined).then(
  (f) => {
    font = f;
    renderPages_();
  },
  (e) => {
    const msg = e instanceof FontMissingError ? e.message : `Font load failed: ${e.message}`;
    $('png-error').textContent = `Page rendering is unavailable. ${msg}`;
    $('png-error').hidden = false;
    fontFailed = true;
    $('page-end').disabled = true;
    $('page-rows').disabled = true;
    $('dl-pdf').disabled = true;
    $('style').disabled = true;
  },
);

// Throttle, not debounce: while the C64 prints without pauses longer than 150 ms a
// debounce would postpone the repaint until the print is over.
function scheduleRender() {
  if (renderTimer) return; // one is already due; it will draw everything received by then
  renderTimer = setTimeout(() => {
    renderTimer = 0;
    renderPages_();
  }, 150);
}

// One persistent card per page: updated in place and only redrawn when its
// content or its displayed size changed, so a growing print job never rebuilds
// (and flickers) the pages that are already complete.
const views = [];
let lastPageCount = 0;

function createView() {
  const label = document.createElement('span');
  const button = Object.assign(document.createElement('button'), { textContent: 'PNG', className: 'outline' });
  button.title = 'Download this page as PNG';
  const head = document.createElement('header');
  head.append(label, button);
  const canvas = document.createElement('canvas');
  const card = Object.assign(document.createElement('article'), { className: 'page' });
  card.append(head, canvas);
  $('pages').append(card);
  const view = { card, label, canvas, bm: null, px: 0, rows: 0, labelText: '' };
  button.onclick = () => downloadPng(view.bm, fileNames.png(jobName(), views.indexOf(view) + 1));
  return view;
}

function updateView(view, index, bm, rows) {
  const prev = view.bm;
  view.bm = bm;
  const text = `Page ${index + 1}`;
  if (text !== view.labelText) {
    view.labelText = text;
    view.label.textContent = text;
  }
  view.label.title = `${bm.width} × ${rows} dots`;
  // Draw at the exact device-pixel size it is displayed at: the browser then
  // never rescales the image, which is what made small previews shimmer.
  const px = Math.round(view.canvas.clientWidth * (window.devicePixelRatio || 1));
  if (px < 1) return; // hidden or not laid out yet; the resize observer retries
  const ctx = view.canvas.getContext('2d');
  if (px !== view.px || rows !== view.rows) {
    // First draw, or the size changed (zoom, window, page length): whole sheet.
    const img = previewGray(bm, px, rows);
    view.canvas.width = img.width;
    view.canvas.height = img.height;
    ctx.putImageData(new ImageData(grayToRGBA(img), img.width, img.height), 0, 0);
    view.px = px;
    view.rows = rows;
    return;
  }
  // Ink is only ever added to paper: paint just the rows that changed, on top
  // of what is already there. Nothing is cleared, nothing flickers.
  const dirty = dirtyTargetRows(prev, bm, px, rows);
  if (!dirty) return;
  const patch = previewRegion(bm, px, rows, dirty[0], dirty[1]);
  ctx.putImageData(new ImageData(grayToRGBA(patch), px, patch.height), 0, dirty[0]);
}

function renderPages_() {
  if (!font) return;
  const rows = Math.min(MAX_PAGE_ROWS, Math.max(MIN_PAGE_ROWS, Number($('page-rows').value) || 792));
  const pages = renderPages(lines, font, rows);
  lastPages = pages;
  lastPageRows = rows;
  $('dl-pdf').disabled = pages.length === 0;
  updatePaperButtons(); // the fit button follows the amount of data
  if (pages.length > lastPageCount && lastPageCount > 0) favicon.pageBreak(); // printing reached the end of a page
  lastPageCount = pages.length;
  while (views.length < pages.length) views.push(createView());
  while (views.length > pages.length) views.pop().card.remove();
  pages.forEach((bm, i) => updateView(views[i], i, bm, rows));
}

// Redraw when the available width changes (window resize, zoom, section shown).
new ResizeObserver(() => scheduleRender()).observe($('pages'));

// Export helpers: the on-screen preview is always the cheap exact-dot bitmap; the
// chosen export style is rendered on demand (a 360 dpi page is ~11 MB).
const exportStyle = () => $('style').value;

function downloadPng(bm, name) {
  const wantNeedle = exportStyle() === 'needle';
  const needle = wantNeedle && bm.height * CELL_Y <= MAX_CANVAS_PX;
  if (wantNeedle && !needle) {
    alert('This page is too tall for a needle style PNG (the browser stops at 65535 pixels): it is saved as exact dots. The PDF keeps the needle style.');
  }
  const img = needle ? needleRender(bm) : bm;
  const canvas = Object.assign(document.createElement('canvas'), { width: img.width, height: img.height });
  const rgba = needle ? grayToRGBA(img) : toRGBA(img);
  canvas.getContext('2d').putImageData(new ImageData(rgba, img.width, img.height), 0, 0);
  canvas.toBlob((blob) => download(name, [blob]), 'image/png');
}

// Preview size: CSS only (the page cards are 480 dots * zoom wide); the exports
// are unaffected. Remembered across visits.
function applyZoom(percent) {
  document.documentElement.style.setProperty('--zoom', percent / 100);
  $('zoom-value').textContent = `${percent}%`;
  scheduleRender(); // re-sample the previews at the new size
}
const savedZoom = Number(localStorage.getItem('printy.zoom'));
if (savedZoom >= 40 && savedZoom <= 200) $('zoom').value = savedZoom;
applyZoom(Number($('zoom').value));
$('zoom').oninput = () => {
  applyZoom(Number($('zoom').value));
  localStorage.setItem('printy.zoom', $('zoom').value);
};

// Page length: free input plus US Letter / A4 presets; remembered across visits.
const PAPER = { letter: 792, a4: 842 };
const MIN_PAGE_ROWS = 50;
const MAX_PAGE_ROWS = 14400; // PDF pages stop at 14400 pt (200 in); a needle PNG is 5 px per row and canvases stop at 65535 px high (13107 rows), taller pages are exported as exact dots (downloadPng)
const MAX_CANVAS_PX = 65535;

function updatePaperButtons() {
  const rows = Number($('page-rows').value);
  $('paper-letter').setAttribute('aria-pressed', String(rows === PAPER.letter));
  $('paper-a4').setAttribute('aria-pressed', String(rows === PAPER.a4));
  const fit = Math.min(MAX_PAGE_ROWS, Math.max(MIN_PAGE_ROWS, contentRows(lines)));
  $('paper-fit').disabled = contentRows(lines) === 0;
  $('paper-fit').setAttribute('aria-pressed', String(contentRows(lines) > 0 && rows === fit));
  $('paper-fit').title = `One page for everything printed so far (${contentRows(lines)} rows, a banner; at most ${MAX_PAGE_ROWS})`;
}

const savedRows = Number(localStorage.getItem('printy.pageRows'));
if (savedRows >= MIN_PAGE_ROWS && savedRows <= MAX_PAGE_ROWS) $('page-rows').value = savedRows;
updatePaperButtons();

let skipSaving = false; // "Fit all" is a one-off for the current printout: not remembered for the next visit
$('page-rows').onchange = () => {
  lastPageCount = 0; // a different page length re-cuts the paper; that is not a page break
  if (!skipSaving) localStorage.setItem('printy.pageRows', $('page-rows').value);
  updatePaperButtons();
  renderPages_();
};
for (const [id, rows] of [['paper-letter', PAPER.letter], ['paper-a4', PAPER.a4]]) {
  $(id).onclick = () => {
    $('page-rows').value = rows;
    $('page-rows').dispatchEvent(new Event('change'));
  };
}
$('paper-fit').onclick = () => {
  $('page-rows').value = Math.min(MAX_PAGE_ROWS, Math.max(MIN_PAGE_ROWS, contentRows(lines)));
  skipSaving = true;
  $('page-rows').dispatchEvent(new Event('change'));
  skipSaving = false;
};
$('dl-pdf').onclick = async () => {
  if (lastPages.length === 0) return;
  const pages = exportStyle() === 'needle' ? lastPages.map((p) => needleRender(p)) : lastPages;
  download(fileNames.pdf(jobName()), [await buildPdf(pages, lastPageRows, paperPdfOptions(lastPageRows))]);
};
$('page-end').onclick = () => {
  if (selected && !selected.remote) book.addEvent(selected, { t: 'pagebreak' }); // recorded in the job; shown through onEvent
};

// state: 'ok' (green dot), 'error' (red) or 'idle' (grey)
function setStatus(t, state = 'idle') {
  $('status').textContent = t;
  $('status').title = t; // full text on hover when it is shortened
  $('status-dot').dataset.state = state;
}

// The UART link cannot see whether a program has the port open (DTR does not reach the chip): the firmware counts
// the host as connected for a few seconds after the last byte it got, so CMD_OPEN (which also starts the framed stream,
// see LinkReceiver) goes out every second.
let keepaliveTimer = null;
function startKeepalive(w) {
  clearInterval(keepaliveTimer);
  const send = () => { if (writer === w && linkRx) w.write(buildFrame(CMD.OPEN, [linkRx.epoch])).catch(() => {}); };
  send();
  keepaliveTimer = setInterval(send, 1000);
}

async function closePort(why) {
  clearInterval(keepaliveTimer);
  const p = port;
  port = null;
  device = null;
  try {
    writer?.releaseLock(); // an open port cannot be closed while its writer is locked
  } catch {
    // already released
  }
  writer = null;
  linkRx = null;
  try {
    await p?.close();
  } catch (e) {
    console.warn('port.close failed', e);
  }
  $('connect').textContent = 'Connect';
  $('setup-menu').hidden = true;
  $('wifi-toggle').hidden = true;
  showSetup(null);
  usbConnected = false;
  syncIcon();
  if (restarting) setStatus('device restarting…', 'idle'); // an expected disconnect, not an error
  else if (why) setStatus(why, /error|failed/.test(why) ? 'error' : 'idle');
}

// Asks the firmware who it is. Firmware older than the command channel never
// answers, which is fine: the page then just works as before.
let lastSetupReady = null; // the setup state seen at the last connection: null = not known yet
let offerTestPrint = false;
$('wizard-dialog').addEventListener('close', () => {
  if (!offerTestPrint) return;
  offerTestPrint = false;
  setTimeout(() => openTestPrint({ download, intro: true }), 50);
});
async function identifyDevice(link) {
  try {
    const info = await link.getInfo();
    if (!info || link !== device) return null;
    if (info.protocol !== PROTOCOL_VERSION) {
      setStatus(`connected, but the firmware speaks protocol v${info.protocol} and this page expects v${PROTOCOL_VERSION}: update one of them`, 'error');
    } else {
      setStatus(`connected (firmware ${info.firmware}${info.ready ? '' : ', not set up yet'})`, info.ready ? 'ok' : 'idle');
      $('setup-menu').hidden = false;
      const stored = await link.getConfig();
      if (link === device) {
        const st = stored ? setupState(stored.config) : null;
        showSetup(st);
        // The setup was just completed: offer a test print (after the wizard's own "Done" dialog is closed)
        if (st && lastSetupReady === false && st.ready) {
          if ($('wizard-dialog').open) offerTestPrint = true;
          else openTestPrint({ download, intro: true });
        }
        if (st) lastSetupReady = st.ready;
      }
      await refreshWifiToggle(link);
    }
    return info;
  } catch {
    // no answer: old firmware without device commands
    return null;
  }
}

// The "Wi-Fi printing" switch: only for a device that has Wi-Fi printing set up. Off = the device streams to this
// page over USB while the connection lasts (not stored on the device).
async function refreshWifiToggle(link) {
  try {
    const st = await link.netStatus();
    if (link !== device) return;
    $('wifi-toggle').hidden = !st?.wifiMode;
    if (st) $('use-wifi').checked = !st.usbMode;
  } catch {
    $('wifi-toggle').hidden = true; // firmware without the command
  }
}

$('use-wifi').onchange = async () => {
  const link = device;
  if (!link) return;
  const usb = !$('use-wifi').checked;
  $('use-wifi').disabled = true;
  $('use-wifi-text').textContent = 'Wi-Fi printing (switching…)';
  try {
    if ((await link.setUsbStream(usb)) !== 0) throw new Error('the device refused it');
    // The device applies it once a running Wi-Fi session is over (the check-in right after it started can take
    // many seconds): wait for that instead of snapping the switch back.
    let applied = false;
    for (let i = 0; i < 120 && link === device && !applied; i++) {
      await new Promise((r) => setTimeout(r, 500));
      try {
        applied = (await link.netStatus())?.usbMode === usb;
      } catch {
        // no answer this time (the device is busy): ask again
      }
    }
    if (!applied) throw new Error('the device did not switch in time');
    if (usb && source !== usbSource) await activateSource(null); // show what arrives over USB now
  } catch (e) {
    if (link === device) setStatus(`could not switch: ${e.message}`, 'error');
  }
  $('use-wifi-text').textContent = 'Wi-Fi printing';
  $('use-wifi').disabled = false;
  if (link === device) await refreshWifiToggle(link);
};

async function readLoop() {
  let why = 'disconnected';
  try {
    await storedLoaded; // stored jobs first, so job ids do not clash
    while (port?.readable) {
      const reader = port.readable.getReader();
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value?.length) linkRx?.push(value);
        }
        break; // stream closed (port.close() or device gone)
      } catch (e) {
        // Recoverable errors (BufferOverrun, Framing, ...) leave port.readable
        // usable and the loop continues; fatal ones (device lost) null it.
        console.error('serial read error', e);
        why = `disconnected (read error: ${e.name}: ${e.message})`;
      } finally {
        reader.releaseLock();
      }
    }
  } finally {
    // Always release the port: a leaked open port stays locked for every
    // other tab/program ("device is busy") until the browser is restarted.
    await closePort(why);
  }
}

// Opens `p` and starts reading from it. Used for the first connection and again
// when the ESP comes back after a restart.
// requireDevice: only count it as connected if the firmware answers (used after a
// restart, where a stale port object must not pass for the device).
async function attachPort(p, { requireDevice = false } = {}) {
  port = p;
  try {
    await port.open({ baudRate: 921600 }); // TRANSPORT_BAUD in firmware/main/transport.h
    // DTR and RTS drive the board's auto-reset circuit (EN and GPIO0 through two transistors): both asserted is the
    // idle state; leaving only RTS asserted would hold the chip in reset.
    await port.setSignals({ dataTerminalReady: true, requestToSend: true });
    $('connect').textContent = 'Disconnect';
    usbConnected = true;
    syncIcon();
    setStatus('connected', 'ok');
    writer = port.writable.getWriter();
    device = new DeviceLink((frame) => writer.write(frame));
    linkRx = new LinkReceiver(crypto.getRandomValues(new Uint8Array(1))[0], {
      onPayload: onStreamBytes,
      onAck: (epoch, boot, next) => writer?.write(buildFrame(CMD.ACK, [epoch, boot, next])).catch(() => {}),
    });
    startKeepalive(writer);
    readLoop();
    const identified = identifyDevice(device);
    if (requireDevice && !(await identified)) throw new Error('the device does not answer');
  } catch (e) {
    console.error('connect failed', e);
    await closePort(`connect failed: ${e.name}: ${e.message}`);
    throw e;
  }
}

$('connect').onclick = async () => {
  if (port) {
    await closePort('disconnected');
    return;
  }
  try {
    await attachPort(await navigator.serial.requestPort({ filters: USB_FILTERS }));
  } catch {
    // attachPort already reported it (or the user dismissed the port chooser)
  }
};

$('flash').onclick = () => openFlasher({ disconnect: () => closePort('disconnected') });

// After the ESP restarts (a setting was saved) the page has to talk to it again. The DevKit's USB-UART bridge keeps
// the port open while the chip restarts, so the connection stays: wait for the chip to boot and ask it until it
// answers. Should the port itself have gone (cable pulled), the permission the user gave stays valid: getPorts()
// returns it and no new chooser is needed. Resolves true once reconnected.
async function reconnectAfterRestart(timeoutMs = 20000) {
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const t0 = Date.now();
  restarting = true;
  setStatus('device restarting…', 'idle');
  try {
    await sleep(1500); // the chip is restarting: it does not answer before it has booted
    while (Date.now() - t0 < timeoutMs) {
      if (port && device) {
        if (await identifyDevice(device)) return true;
      } else {
        const candidates = (await navigator.serial.getPorts()).filter((p) => {
          const info = p.getInfo?.() ?? {};
          return USB_FILTERS.some((f) => f.usbVendorId === info.usbVendorId && f.usbProductId === info.usbProductId);
        });
        for (const p of candidates) {
          try {
            await attachPort(p, { requireDevice: true }); // while `restarting` a failed attempt is not shown as an error
            return true;
          } catch {
            // not ready yet (still starting up): try again
          }
        }
      }
      await sleep(500);
    }
    return false;
  } finally {
    restarting = false;
  }
}

// The "Set up" menu: close the dropdown, then open the chosen wizard.
function menuItem(id, open) {
  $(id).onclick = (e) => {
    e.preventDefault();
    $('setup-menu').querySelector('details').removeAttribute('open');
    open();
  };
}
const openLed = () => openLedWizard({ getDevice: () => device, reconnect: reconnectAfterRestart });
const openButton = () =>
  openButtonWizard({
    getDevice: () => device,
    reconnect: reconnectAfterRestart,
    setPressListener: (fn) => (pinPressListener = fn),
  });
const openIec = () => openIecWizard({ getDevice: () => device, reconnect: reconnectAfterRestart });
menuItem('setup-led', openLed);
menuItem('setup-button', openButton);
menuItem('setup-iec', openIec);
const openWifi = () =>
  openWifiSetup({
    getDevice: () => device,
    reconnect: reconnectAfterRestart,
    keyStore,
    onDone: (rec) => activateSource(rec),
  });
menuItem('setup-wifi', openWifi);
menuItem('setup-testprint', () => openTestPrint({ download }));
$('todo-button').onclick = openButton;
$('todo-led').onclick = openLed;
$('todo-iec').onclick = openIec;

navigator.serial?.addEventListener('disconnect', () => {
  usbConnected = false;
  syncIcon();
  setStatus('device unplugged', 'error');
});

async function replay(name, bytes) {
  await storedLoaded;
  if (source.kind === 'server') await activateSource(null); // a replay goes into the local (USB) jobs
  wire.push(bytes);
  wireBytes += bytes.length;
  parser.push(bytes);
  updateRawSize();
  renderText();
  render();
  setStatus(`replayed ${name} (${bytes.length} bytes)`, 'ok');
}

// ?replay=samples/text-test.wire.bin loads a capture on start (development aid).
const replayUrl = new URLSearchParams(location.search).get('replay');
if (replayUrl) {
  fetch(replayUrl)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((b) => replay(replayUrl, new Uint8Array(b)))
    .catch((e) => setStatus(`replay failed: ${e.message}`, 'error'));
}

$('load-wire').onchange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const bytes = new Uint8Array(await file.arrayBuffer());
  await replay(file.name, bytes);
  e.target.value = '';
};

$('load-vice').onchange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const raw = new Uint8Array(await file.arrayBuffer());
  await replay(file.name, viceRawToWire(raw));
  e.target.value = '';
};

$('dl-all').onclick = () => download(fileNames.rawAll(jobName()), transfers.map((t) => t.data));
$('dl-text').onclick = () =>
  download(fileNames.text(jobName()), [new Blob([textContent() + '\n'], { type: 'text/plain;charset=utf-8' })]);
$('dl-wire').onclick = () => download(fileNames.wire(jobName()), wire);
// A D64 with the REPLAY program (replay.asm) and the job as DATA.SEQ: load it on a C64 and the job is printed again.
$('dl-replay').onclick = async () => {
  try {
    const res = await fetch(assetUrl('replay.prg'));
    if (!res.ok) throw new Error(`replay.prg not found (${res.status}): run make replay in web/`);
    const disk = buildReplayDisk(selected?.events ?? [], new Uint8Array(await res.arrayBuffer()));
    if (!disk) throw new Error('this job has nothing to print');
    download(fileNames.replay(jobName()), [disk]);
  } catch (e) {
    setSourceStatus(`Replay disk: ${e.message}`, true);
  }
};
$('clear').onclick = async () => {
  const ok = await askConfirm({
    title: 'Delete all jobs?',
    message: source.kind === 'server' ? 'This deletes all jobs of this printer from the print server. Download what you need first.' : 'This discards all jobs with their pages, text and raw data. Download what you need first.',
    confirmLabel: 'Delete all',
  });
  if (!ok) return;
  favicon.pageBreak();
  try {
    await source.clear();
  } catch (e) {
    sourceError(e);
    return;
  }
  if (source.kind === 'usb') {
    wire.length = 0;
    wireBytes = 0;
  }
  selectJob(null, { follow: true });
};
